/**
 * Reference-style horizontal info strip below the header:
 *   Response Time · Resolution Time · Created At · Last Modified At ·
 *   Last Modified By · Status (editable dropdown)
 *
 * Mirrors the KC2 interaction view's details strip. All corners ≤3px.
 */
import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react'
import { ChevronDown, Lock } from 'lucide-react'
import type { ActivityLogEntry, ConversationDetailResponse } from '../../../modules/conversations/types.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { useConversationStatuses } from '../../../hooks/useConversationStatuses.js'
import { presentConversationStatus, statusBucket, statusKey, statusStyles } from '../../../modules/conversations/statusCatalog.js'
import { computeSlaTimer } from './sla.js'
import { R } from './primitives.js'

type Conversation = ConversationDetailResponse['conversation']

function Cell({
  label,
  children,
  last,
}: {
  label: string
  children: ReactNode
  last?: boolean
}): JSX.Element {
  return (
    <div className={`min-w-0 flex-1 px-4 py-2.5 ${last ? '' : 'border-r border-slate-200'}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 truncate text-sm text-slate-800">{children}</div>
    </div>
  )
}

/* ── Editable status dropdown ──
 * Options are the tenant's own statuses from `conversation_status`; nothing
 * here is hardcoded, and every configured status can be picked. Rows whose name
 * maps to a lifecycle position (open/re-open, on hold, closed) go through the
 * dedicated endpoint for that transition — closing, for instance, still opens
 * the close drawer for its resolution fields. Any other status (e.g. "In
 * Progress") is saved straight onto the conversation.
 *
 * Read-only unless the conversation is yours. The API enforces the same rule
 * (assignee or supervisor), so this is about not offering an
 * action that would only come back as a 403. */
function StatusDropdown({
  status,
  conversationStatusId,
  statusName,
  statusColor,
  canChangeStatus,
  lockedReason,
  onReopen,
  onHold,
  onOpenCloseDrawer,
  onSelectStatus,
}: {
  status: string
  conversationStatusId: string | null
  statusName: string | null
  statusColor: string | null
  canChangeStatus: boolean
  lockedReason?: string | undefined
  onReopen: () => void
  onHold: () => void
  onOpenCloseDrawer: () => void
  onSelectStatus: (statusId: string) => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { statuses, loading } = useConversationStatuses()
  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const current = presentConversationStatus(statuses, {
    status,
    conversationStatusId,
    statusName,
    statusColor,
  })
  const currentStyles = statusStyles(current.color)
  const currentBucket = statusBucket(status)

  /** A row that moves the conversation to a different lifecycle position runs that
   *  transition (with its side effects — resolution fields on close, SLA restart
   *  on reopen). Anything else — a custom status, or a lifecycle row the conversation
   *  is already in — is a straight catalog assignment. */
  const actionFor = (option: { id: string; name: string }): (() => void) => {
    const key = statusKey(option.name)
    if (statusBucket(key) !== currentBucket) {
      if (key === 'open' || key === 'reopen') return onReopen
      if (key === 'on_hold') return onHold
      if (key === 'closed') return onOpenCloseDrawer
    }
    return () => onSelectStatus(option.id)
  }

  // "Open" and "Re Open" are the same lifecycle position, so listing both reads
  // as two ways to do one thing. Offer the re-open row — the one an agent
  // actually picks — and fall back to "Open" for a tenant that has no re-open
  // status configured.
  const hasReopen = statuses.some((o) => statusKey(o.name) === 'reopen')
  const selectable = statuses.filter((o) => !(hasReopen && statusKey(o.name) === 'open'))

  const options = selectable.map((option) => ({
    id: option.id,
    label: option.name,
    styles: statusStyles(option.color),
    onClick: actionFor(option),
    disabled: option.id === conversationStatusId,
  }))

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={!canChangeStatus}
        title={canChangeStatus ? undefined : lockedReason}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between ${R} border border-slate-200 px-2.5 py-1.5 text-left text-sm text-slate-800 transition-colors ${
          canChangeStatus
            ? 'bg-white hover:border-slate-300'
            : 'cursor-not-allowed bg-slate-50 text-slate-500'
        }`}
      >
        <span className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 rounded-full ${current.color ? '' : 'bg-slate-400'}`}
            style={currentStyles.dot}
          />
          {current.label}
        </span>
        {canChangeStatus ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <Lock className="h-3 w-3 shrink-0 text-slate-400" />
        )}
      </button>
      {open && canChangeStatus && (
        <div className={`absolute right-0 z-30 mt-1 w-44 overflow-hidden ${R} border border-slate-200 bg-white shadow-xl`}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">
              {loading ? 'Loading statuses…' : 'No statuses configured'}
            </div>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                disabled={o.disabled}
                title={o.disabled ? 'The conversation is already in this status' : undefined}
                onClick={() => {
                  setOpen(false)
                  o.onClick()
                }}
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${o.styles.dot.background ? '' : 'bg-slate-400'}`}
                  style={o.styles.dot}
                />
                <span className="truncate">{o.label}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export interface DetailStripProps {
  conversation: Conversation
  activityLog: ActivityLogEntry[]
  /** False when the conversation belongs to another agent — the status dropdown
   *  becomes read-only, mirroring the API's ownership check. */
  canChangeStatus?: boolean
  onReopen: () => void
  onHold: () => void
  onOpenCloseDrawer: () => void
  /** Picks a catalog status that needs no lifecycle transition. */
  onSelectStatus: (statusId: string) => void
}

export default function DetailStrip({
  conversation,
  activityLog,
  canChangeStatus = true,
  onReopen,
  onHold,
  onOpenCloseDrawer,
  onSelectStatus,
}: DetailStripProps): JSX.Element {
  const now = Date.now()
  const response = computeSlaTimer(conversation.firstResponseDueAt, conversation.firstRespondedAt, now)
  const resolution = computeSlaTimer(conversation.resolutionDueAt, conversation.resolvedAt, now)

  // Newest activity (API returns DESC) → last modified.
  //
  // Only a real agent's own action counts as "who" modified the conversation
  // (actorType 'agent' — matches the Activity tab's actorName for that row).
  // Every non-agent actor reads as "System" here, even though actorName may
  // carry more detail for audit purposes (an automation rule's name, etc.) —
  // that detail belongs in the Activity tab, not this summary field, or a
  // rule name displays as if it were a person. The one exception: when the
  // trigger came from outside — an email or social message being ingested —
  // the person who actually moved the conversation is the customer, so they
  // are named instead of a bare "System".
  const latest = activityLog[0]
  const modifiedAt = latest?.createdAt ?? conversation.lastMessageAt
  const actorType = latest?.details?.['actorType']
  const modifiedByRaw = latest?.details?.['actorName']
  const customerName = conversation.displayName || conversation.primaryHandle || ''
  const isAgentActor = actorType === 'agent' && typeof modifiedByRaw === 'string' && modifiedByRaw
  // message_received is logged with actor.type 'system' (the ingest pipeline
  // did the writing), but the party who actually caused the change is the
  // customer whose message arrived — there is no separate 'customer' actorType.
  const isCustomerTriggered = latest?.eventType === 'message_received'
  const modifiedBy = !latest
    ? '—'
    : isAgentActor
      ? (modifiedByRaw as string)
      : isCustomerTriggered
        ? customerName || 'System'
        : 'System'

  return (
    <div className="flex flex-wrap items-stretch border-b border-slate-200 bg-white">
      <Cell label="Response Time">
        {conversation.firstResponseDueAt ? (
          <span className={`inline-flex items-center gap-1 ${response.tone.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${response.tone.dot}`} />
            {response.label}
          </span>
        ) : (
          <span className="text-slate-400">No SLA Set</span>
        )}
      </Cell>
      <Cell label="Resolution Time">
        {conversation.resolutionDueAt ? (
          <span className={`inline-flex items-center gap-1 ${resolution.tone.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${resolution.tone.dot}`} />
            {resolution.label}
          </span>
        ) : (
          <span className="text-slate-400">No SLA Set</span>
        )}
      </Cell>
      <Cell label="Created At">{formatDateTime(conversation.openedAt) || '—'}</Cell>
      <Cell label="Last Modified At">{formatDateTime(modifiedAt) || '—'}</Cell>
      <Cell label="Last Modified By">{modifiedBy}</Cell>
      <div className="w-48 shrink-0 px-4 py-2 border-l border-slate-200">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status</div>
        <StatusDropdown
          status={conversation.status}
          conversationStatusId={conversation.conversationStatusId}
          statusName={conversation.conversationStatusName}
          statusColor={conversation.conversationStatusColor}
          canChangeStatus={canChangeStatus}
          lockedReason="Self-assign this conversation to change its status"
          onReopen={onReopen}
          onHold={onHold}
          onOpenCloseDrawer={onOpenCloseDrawer}
          onSelectStatus={onSelectStatus}
        />
      </div>
    </div>
  )
}
